import BaseObject from "../../../base/base_object";
import Event, {SystemEventSubscription} from "../../../utils/event";
import {SYSTEM_EVENTS} from "../../../events/events";
import ServiceFactory from "../../service_factory";
import ReconnectHelper, {IReconnectHelper, IReconnectHelperParams, RetryItem} from "../../../utils/reconnect_helper";
import _ from "../../../utils/underscore";
import FirebaseHandlerBase, {IBaseParams} from "./firebase_handler_base";
import {IMessagesParams} from "./firebase_const";
import {ITokenResponse} from "../../oauth/user/user_service_interface";
import {Check} from "@sparkware/app-utils";

export interface IFirebaseParams {
    serviceFactory: ServiceFactory;
    libUrl: string;
    OMGConfig: IMessagesParams;
    FPConfig?: IBaseParams;
    reconnectionHelperFactory?: () => ReconnectHelper;
    forceFallback?: boolean;
}

export const RETRY_INTERVALS: RetryItem[] = [
  {durationInSeconds:5, repeatTimes:3},
  {durationInSeconds:10, repeatTimes:3},
  {durationInSeconds:120, repeatTimes:0}
];

export default class FirebaseStrategy extends BaseObject {
    /**
     * @static
     * @type {string}
     */
    static CLASS = "FirebaseStrategy";

    private _firebaseConnectionFactory;
    private _firebaseConnection;
    private _initParams: IFirebaseParams;
    private _reconnectHelper: IReconnectHelper;
    private _firebaseHandlers: FirebaseHandlerBase[] = [];
    private _sendMonitoringRetriesNo: number[] = [];
    private _onOAuthStateChangedSubscription: SystemEventSubscription;
    private _onInitCompletedSubscription: SystemEventSubscription;

    init(params: IFirebaseParams) {
        super.init(params);
        this._initParams = params;
        this.setKeyRetries();
        this._reconnectionHelperFactory = params.reconnectionHelperFactory || this._reconnectionHelperFactory;
        this._subscribeToInitComplete();
    }

    private _subscribeToInitComplete() {
        if (!this._onInitCompletedSubscription) {
            this._onInitCompletedSubscription = Event.on(SYSTEM_EVENTS.ON_INIT_COMPLETE, this._onInitComplete.bind(this), this);
        }
    }
    private _subscribeToOAuthStateChanged() {
        if (!this._onOAuthStateChangedSubscription) {
            this._onOAuthStateChangedSubscription = Event.on(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onAuthStateChanged.bind(this), this);
        }
    }

    private _onInitComplete() {
        if (this._isAuthorized()) {
            this._getFirebaseAndConnect()
                .then(() => this._subscribeToOAuthStateChanged())
                .catch(() => this._subscribeToOAuthStateChanged());
        } else {
            this._subscribeToOAuthStateChanged();
        }
    }

    private _onAuthStateChanged() {
        if (this._isAuthorized()) {
            this._getFirebaseAndConnect()
        } else {
            this._disconnect();
        }
    }

    private async _getFirebaseAndConnect(): Promise<void> {
        await this._initParams.serviceFactory.oAuth2Service().waitForAutologin();
        if (!this._firebaseConnectionFactory) {
            try {
                await this._loadTripleMessaging();
                this._firebaseConnectionFactory = this._getFirebaseConnectorFromWindow();
            } catch (err) {
                this._startFallbackService();
                throw err;
            }
        }
        this._startConnectFirebase();
    }

    private async _connectToFirebase(): Promise<boolean> {
        let token;
        try {
            token = await this._getToken();
            this._initParams.serviceFactory.userService().setFirebaseLoginToken(token.token);
            this._firebaseConnection = await this._firebaseConnectionFactory.createWithCallback(token.token, (data) => this._enhanceAndReportFirebaseSDKEvent(data), {});
        } catch (err) {
            this._sendFailMonitoring(err);
            if (this._reconnectHelper.getNumberOfRetries() === 2) {
                this._startFallbackService();
            }
            this.logger.warn('Connect to Firebase failed.', err);
            throw err;
        }

        Event.fire(SYSTEM_EVENTS.FIREBASE_GOT_NEW_TOKEN, {token: token.token});
        Event.fire(SYSTEM_EVENTS.PLAYER_MESSAGES_STRATEGY_READY);
        this._initParams.serviceFactory.fallbackService().stopAllButThis();
        this._subscribeToFirebaseEvents();
        this.logger.info('Firebase CONNECTED');
        this._initParams.serviceFactory.firebaseElasticService().sendConnected();

        return true;
    }

    private _loadTripleMessaging():Promise<void>{
        return this._initParams.serviceFactory.scriptLoaderService().loadScript(this._initParams.libUrl, "triple8MessagingLibrary");
    }

    private _getFirebaseConnectorFromWindow(){
        return this._initParams.serviceFactory.windowService().getGlobalProperty('triple8Messaging');
    }

    private _subscribeToFirebaseEvents() {
        this._firebaseHandlers = this._readHandlers();
    }

    private _readHandlers(): FirebaseHandlerBase[] {
        const context = require.context('./handlers/', true, /^.*(handler)\.[jt]s$/);
        return context.keys().map(context).map((moduleExport: any) => {
            return moduleExport.default.create({
                firebaseMessaging: this._firebaseConnection,
                config: this._initParams
            });
        });
    }

    private _getToken():Promise<ITokenResponse> {
        return this._initParams.serviceFactory.userService().renewLoginToken({
            TargetType: 4,
            TargetTypeValue: "firebase"
        });
    }

    private _disconnect() {
        if (this._firebaseConnectionFactory) {
            if (_.isFunction(this._firebaseConnectionFactory.logOut)) {
                this._firebaseConnectionFactory.logOut();
            } else {
                this._firebaseHandlers.forEach(h => h.unsubscribe());
            }
        }

        this._firebaseConnection = null;
        this._removeReconnectHelper();
        this.logger.info('Firebase DISCONNECTED');
    }

    private _isAuthorized() {
        return this._initParams.serviceFactory.oAuth2Service().isAuthorized();
    }

    //// RECONNECT HELPER

    private _reconnectionHelperFactory(params: IReconnectHelperParams): IReconnectHelper {
        return ReconnectHelper.create(params);
    }

    private _startConnectFirebase() {
        this._removeReconnectHelper();
        if (!this._reconnectHelper) {
            this._reconnectHelper = this._createReconnectionHelper();
        }
        this._reconnectHelper.start();
    }

    private _createReconnectionHelper() {
        return this._reconnectionHelperFactory({
            retryFunc: () => this._connectToFirebase(),
            retryIntervals: RETRY_INTERVALS,
            name: 'FIREBASE',
        });
    }

    private _removeReconnectHelper() {
        if (!_.isNullOrUndefined(this._reconnectHelper)) {
            this._reconnectHelper.stop();
            this._reconnectHelper = null;
        }
    }

    private _startFallbackService() {
        this._initParams.serviceFactory.fallbackService().start();
        this.logger.info('Firebase Connection Fail, start Fallback Service');
        this._initParams.serviceFactory.firebaseElasticService().sendFallbackStarted("All")
    }

    private _sendFailMonitoring(err: any) {
        if (this._sendMonitoringRetriesNo.includes(this._reconnectHelper.getNumberOfRetries())) {
            this._initParams.serviceFactory.firebaseElasticService().sendConnectionFailed(this._reconnectHelper.getNumberOfRetries() + 1, JSON.stringify(err))
        }
    }

    private setKeyRetries() {
        let crt = 0;
        this._sendMonitoringRetriesNo = [crt];
        for (const interval of RETRY_INTERVALS) {
            if (interval.repeatTimes !== 0) {
                crt += interval.repeatTimes;
                this._sendMonitoringRetriesNo.push(crt);
            }
        }
    }

    private _enhanceAndReportFirebaseSDKEvent(data) {
        try {
            if(!Check.isObject(data)){
                console.log("Failed on call to _enhanceAndReportEvent. The data parameter is not an object");
                return;
            }

            if(!data["eventHeader"] || !Check.isObject(data["eventHeader"])){
                console.log("Failed on call to _enhanceAndReportEvent. The data object does not contain a valid eventHeader object");
                return;
            }
            Object.assign(data["eventHeader"],{target:"platform-firebase"});

            const sessionData = {
                globalSessionID: this._initParams.serviceFactory.oAuth2Service().getToken().initialSessionId,
                playerSessionID: this._initParams.serviceFactory.oAuth2Service().getToken().sessionId
            }
            const userData = {
                    cid: this._initParams.serviceFactory.userService().getCID(),
                    brand: this._initParams.serviceFactory.skinService().getBrandName(),
                    brandId: this._initParams.serviceFactory.skinService().getBrandID()
            }
            const bakedEvent = Object.assign({userData:userData,sessionData:sessionData},data);
            const storageUrl = this._initParams.serviceFactory.configService().getConfig().StructuredLogging.storageUrl;
            this._initParams.serviceFactory.http2Service().postJSON(storageUrl, {body : bakedEvent,keepalive : true});
        }
        catch (err) {
            console.log("Failed on call to _enhanceAndReportEvent. Error: " + err);
        }
    }
}